import { useEffect, useId, useState } from "react";
import { FormControlLabel, RadioGroup, Typography } from "@mui/material";
import { CoreModal, CorePaper, CoreRadio, CoreTextField, CoreTitleCard } from "../../core";
import styles from "./selectAccommodationModal.module.css";
import { ProctoringOptionProfileV2, OptionRelatedInstituteSettings } from "@verity/proctoring-option/types";
import { VerityInstituteSettings } from "../..";
import i18nPackage from "../../i18n/i18n";
import ProfileSection from "../profileSection/profileSection";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";

export type SelectAccommodationProps = {
	open: boolean;
	profileList: Array<ProctoringOptionProfileV2>;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	activeProfileId: number | undefined;
	studentAccommodationNotes: string | undefined;
	lmsCourseId?: string;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
	enforcedProfilesOnly: boolean;
	hasInstituteProfilePermissions: boolean;
	saveHandler: (selectedProfileId: number | undefined, selectedNotes: string | undefined) => void;
	onClose: () => void;
};

const SelectAccommodationModal = ({
	open,
	profileList,
	refreshProfiles,
	activeProfileId,
	studentAccommodationNotes,
	lmsCourseId,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
	enforcedProfilesOnly,
	hasInstituteProfilePermissions,
	saveHandler,
	onClose,
}: SelectAccommodationProps) => {
	const [selectedProfileId, setSelectedProfileId] = useState(activeProfileId);
	const [showProfilesTab, setShowProfilesTab] = useState(false);
	const [selectedNotes, setSelectedNotes] = useState(studentAccommodationNotes || undefined);
	const [useProfile, setUseProfile] = useState(!!activeProfileId);
	const isMobileLayout = useMobileLayout();

	const handleUseProfileToggle = (val: boolean) => {
		if (val !== useProfile) {
			if (useProfile) {
				setSelectedProfileId(undefined);
			}
			setUseProfile(!useProfile);
		}
	};

	useEffect(() => {
		setShowProfilesTab(useProfile);
	}, [useProfile]);

	const accommodationRadioAriaId = useId();
	const addProfileRadioAriaId = useId();

	const profilesComponent = (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.profileSection} sx={{ padding: "10px" }}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div className={styles.profileSection}>{children}</div>}
		>
			<Typography variant="h2" className={styles.profileSectionHeader}>
				{i18nPackage.t("editQuizPage.accommodationModal.addProfileHeader")}
			</Typography>
			<div className={styles.profileSectionContent}>
				<Typography className={styles.profileSectionContentHeader}>
					{i18nPackage.t("editQuizPage.accommodationModal.addProfileLabel")}
				</Typography>
				<div className={styles.profileSectionContentList}>
					<ProfileSection
						radioGroupAriaId={addProfileRadioAriaId}
						profileList={profileList}
						selectedProfileId={selectedProfileId}
						setSelectedProfileId={setSelectedProfileId}
						refreshProfiles={refreshProfiles}
						lmsCourseId={lmsCourseId}
						hasInstituteProfilePermissions={hasInstituteProfilePermissions}
						optionRelatedInstituteSettings={optionRelatedInstituteSettings}
						verityInstituteSettings={verityInstituteSettings}
						enforcedProfilesOnly={enforcedProfilesOnly}
					/>
				</div>
			</div>
		</ConditionalWrapper>
	);

	const notesComponent = (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.notesSection} sx={{ padding: "10px" }}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div className={styles.notesSection}>{children}</div>}
		>
			<Typography variant="h2" className={styles.notesSectionHeader}>
				{i18nPackage.t("editQuizPage.accommodationModal.notesHeader")}
			</Typography>
			<div className={styles.notesSectionContent}>
				<div className={styles.notesSectionContentHeader}>
					{i18nPackage.t("editQuizPage.accommodationModal.notesLabel")}
				</div>
				<CoreTextField
					id="accommodation_notes_input"
					name="selectedNotesInput"
					value={selectedNotes}
					label={i18nPackage.t("editQuizPage.accommodationModal.textFieldLabel")}
					onChange={(e) => setSelectedNotes(e.target.value)}
					className={styles.notesSectionContentTextBox}
					style={{ width: "100%" }}
					multiline
					InputProps={{
						style: {
							height: "100%",
							width: "100%",
							margin: "0px",
							fontSize: "15px",
							textAlign: "left",
						},
						inputComponent: "textarea",
						inputProps: {
							style: {
								lineHeight: "1.5",
								textAlign: "left",
								height: "100%",
								width: "100%",
								fontSize: "15px",
							},
						},
					}}
					InputLabelProps={{
						style: {
							fontSize: "15px",
							color: "#696969",
						},
					}}
				/>
			</div>
		</ConditionalWrapper>
	);

	return (
		<CoreModal
			open={open}
			onClose={() => {}}
			onTopClose={onClose}
			title={isMobileLayout ? undefined : i18nPackage.t("editQuizPage.accommodationModal.title")}
			minWidth={isMobileLayout ? undefined : "min(87.5%, 780px)"}
			minHeight={isMobileLayout ? undefined : "min(85%, 624px)"}
			widthFixed
			heightFixed={isMobileLayout}
			padding={isMobileLayout ? undefined : "16px 16px 4px 16px"}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.accommodationModal.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.accommodationModal.save"),
					color: "primary",
					onClick: () => {
						saveHandler(selectedProfileId, selectedNotes);
						onClose();
					},
					disabled: useProfile && !selectedProfileId,
				},
			]}
		>
			<div className={`${styles.modalPage} ${isMobileLayout ? styles.modalPageCompact : ""}`}>
				{isMobileLayout ? (
					<CoreTitleCard
						title={i18nPackage.t("editQuizPage.accommodationModal.title")}
						description={i18nPackage.t("editQuizPage.accommodationModal.modalHeader")}
						style={{ minWidth: "300px", padding: "15px 10px 10px 10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
				) : (
					<Typography className={styles.modalHeader}>
						{i18nPackage.t("editQuizPage.accommodationModal.modalHeader")}
					</Typography>
				)}
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper component="section" className={styles.setSection} sx={{ padding: "10px" }}>
							{children}
						</CorePaper>
					)}
					whenFalse={(children) => <div className={styles.setSection}>{children}</div>}
				>
					<Typography variant="h2" className={styles.setSectionHeader}>
						{i18nPackage.t("editQuizPage.accommodationModal.setAccommodation")}
					</Typography>
					<div className={styles.setSectionContent}>
						<Typography className={styles.setSectionContentHeader} id={accommodationRadioAriaId}>
							{i18nPackage.t("editQuizPage.accommodationModal.section1Header")}
						</Typography>
						<div className={styles.setSectionContentOptions}>
							<RadioGroup
								value={useProfile ? "useProfile" : "doNotProctor"}
								onChange={(e) => handleUseProfileToggle(e.target.value === "useProfile")}
								aria-labelledby={accommodationRadioAriaId}
							>
								<FormControlLabel
									control={<CoreRadio value="doNotProctor" forceTabIndex={false} />}
									label={
										<div className={styles.setSectionContentOptionBody}>
											<Typography className={styles.setSectionContentOptionBodyHeader}>
												{i18nPackage.t("editQuizPage.accommodationModal.radio1Title")}
											</Typography>
											<Typography className={styles.setSectionContentOptionBodyDescription}>
												{i18nPackage.t("editQuizPage.accommodationModal.radio1Label")}
											</Typography>
										</div>
									}
									sx={{ m: isMobileLayout ? "0px" : undefined, mb: 2 }}
								/>
								<FormControlLabel
									control={<CoreRadio value="useProfile" forceTabIndex={false} />}
									label={
										<div className={styles.setSectionContentOptionBody}>
											<Typography className={styles.setSectionContentOptionBodyHeader}>
												{i18nPackage.t("editQuizPage.accommodationModal.radio2Title")}
											</Typography>
											<Typography className={styles.setSectionContentOptionBodyDescription}>
												{i18nPackage.t("editQuizPage.accommodationModal.radio2Label")}
											</Typography>
										</div>
									}
									sx={{ m: isMobileLayout ? "0px" : undefined }}
								/>
							</RadioGroup>
						</div>
					</div>
				</ConditionalWrapper>
				{isMobileLayout ? (
					<>
						{notesComponent}
						{showProfilesTab && profilesComponent}
					</>
				) : (
					<>
						{showProfilesTab && profilesComponent}
						{notesComponent}
					</>
				)}
			</div>
		</CoreModal>
	);
};

export default SelectAccommodationModal;
